import React from "react";
import { Box, Chip, Typography, LinearProgress, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getStatusTimeout } from "../../config/orderStatusConfig";

interface FundsBlockIndicatorProps {
  fundsBlocked: boolean;
  fundsDistributed: boolean;
  blockedAt?: string;
  distributedAt?: string;
}

/**
 * Индикатор блокировки средств
 */
export const FundsBlockIndicator: React.FC<FundsBlockIndicatorProps> = ({
  fundsBlocked,
  fundsDistributed,
  blockedAt,
  distributedAt,
}) => {
  const { t } = useTranslation();

  if (!fundsBlocked && !fundsDistributed) {
    return null;
  }

  if (fundsBlocked) {
    return (
      <Tooltip
        title={t("funds_frozen_tooltip", {
          date: blockedAt ? new Date(blockedAt).toLocaleString() : "",
        })}
      >
        <Chip
          icon={<LockIcon />}
          label={t("funds_frozen")}
          size="small"
          sx={{
            bgcolor: "warning.light",
            color: "warning.dark",
            fontWeight: 500,
            "& .MuiChip-icon": {
              color: "warning.dark",
            },
          }}
        />
      </Tooltip>
    );
  }

  if (fundsDistributed) {
    return (
      <Tooltip
        title={t("funds_distributed_tooltip", {
          date: distributedAt ? new Date(distributedAt).toLocaleString() : "",
        })}
      >
        <Chip
          icon={<LockOpenIcon />}
          label={t("funds_distributed")}
          size="small"
          sx={{
            bgcolor: "success.light",
            color: "success.dark",
            fontWeight: 500,
            "& .MuiChip-icon": {
              color: "success.dark",
            },
          }}
        />
      </Tooltip>
    );
  }

  return null;
};

/**
 * Статус таймаута
 */
export type TimeoutStatus = "normal" | "warning" | "critical" | "expired";

interface TimeoutProgressBarProps {
  startTime?: string;
  timeoutMinutes: number;
  statusLabel?: string;
}

/**
 * Прогресс-бар таймаута
 */
export const TimeoutProgressBar: React.FC<TimeoutProgressBarProps> = ({
  startTime,
  timeoutMinutes,
  statusLabel: _statusLabel,
}) => {
  const { t } = useTranslation();
  const statusLabel = _statusLabel || t("timer_timeout");
  if (!startTime) {
    return null;
  }

  const start = new Date(startTime).getTime();
  const now = Date.now();
  const timeout = timeoutMinutes * 60 * 1000;

  const elapsed = now - start;
  const remaining = timeout - elapsed;
  const progress = Math.min(elapsed / timeout, 1);

  let status: TimeoutStatus;
  let color: string;
  let icon: React.ReactNode;
  let statusText: string;

  if (remaining < 0) {
    status = "expired";
    color = "error.main";
    icon = <ErrorIcon fontSize="small" />;
    statusText = t("timer_overdue");
  } else {
    const remainingPercent = remaining / timeout;
    if (remainingPercent < 0.1) {
      status = "critical";
      color = "error.main";
      icon = <ErrorIcon fontSize="small" />;
      statusText = t("timer_critical");
    } else if (remainingPercent < 0.5) {
      status = "warning";
      color = "warning.main";
      icon = <WarningIcon fontSize="small" />;
      statusText = t("timer_warning");
    } else {
      status = "normal";
      color = "success.main";
      icon = <CheckCircleIcon fontSize="small" />;
      statusText = t("timer_normal");
    }
  }

  const formatRemaining = (ms: number) => {
    if (ms < 0) return t("timer_overdue");
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);

    if (hours > 0) {
      return t("time_format_hours", { hours, minutes });
    } else if (minutes > 0) {
      return t("time_format_minutes", { minutes, seconds });
    } else {
      return t("time_format_seconds", { seconds });
    }
  };

  return (
    <Box sx={{ width: "100%", mt: 1, mb: 1 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={0.5}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          {icon}
          <Typography variant="caption" fontWeight={600} sx={{ color }}>
            {statusText}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {t("timer_remaining", { time: formatRemaining(remaining) })}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress * 100}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: "grey.200",
          "& .MuiLinearProgress-bar": {
            bgcolor: color,
            borderRadius: 3,
          },
        }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        mt={0.5}
      >
        {statusLabel}
      </Typography>
    </Box>
  );
};

/**
 * Получение конфигурации таймаута для статуса
 */
export const getTimeoutConfig = (
  status: string,
): { timeoutMinutes: number; label: string } | null => {
  const timeoutMinutes = getStatusTimeout(status);
  if (!timeoutMinutes) return null;
  const labelKey = `timeout_label_${status}`;
  const labelBase = i18next.t(labelKey, { defaultValue: i18next.t("timer_timeout") });
  const suffix = timeoutMinutes >= 60
    ? `(${i18next.t("time_format_hours", { hours: Math.floor(timeoutMinutes / 60), minutes: timeoutMinutes % 60 })})`
    : `(${i18next.t("time_format_minutes_only", { minutes: timeoutMinutes })})`;
  return { timeoutMinutes, label: `${labelBase} ${suffix}` };
};

/**
 * Комбинированный индикатор для заказа
 */
interface OrderStatusIndicatorsProps {
  status: string;
  fundsBlocked: boolean;
  fundsDistributed: boolean;
  assignedAt?: string;
  workStartedAt?: string;
  workCompletedAt?: string;
  blockedAt?: string;
  distributedAt?: string;
}

export const OrderStatusIndicators: React.FC<OrderStatusIndicatorsProps> = ({
  status,
  fundsBlocked,
  fundsDistributed,
  assignedAt,
  workStartedAt,
  workCompletedAt,
  blockedAt,
  distributedAt,
}) => {
  const timeoutConfig = getTimeoutConfig(status);

  let startTime: string | undefined;
  switch (status) {
    case "assigned":
      startTime = assignedAt;
      break;
    case "in_progress":
      startTime = workStartedAt;
      break;
    case "completed":
      startTime = workCompletedAt;
      break;
  }

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {/* Индикатор блокировки */}
      <FundsBlockIndicator
        fundsBlocked={fundsBlocked}
        fundsDistributed={fundsDistributed}
        blockedAt={blockedAt}
        distributedAt={distributedAt}
      />

      {/* Прогресс-бар таймаута */}
      {timeoutConfig && startTime && (
        <TimeoutProgressBar
          startTime={startTime}
          timeoutMinutes={timeoutConfig.timeoutMinutes}
          statusLabel={timeoutConfig.label}
        />
      )}
    </Box>
  );
};

export default OrderStatusIndicators;

// Prevent tree-shaking
if (typeof window !== "undefined") {
  (window as any).__OrderStatusIndicators = OrderStatusIndicators;
}
